import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { Section, SettingRow, NumberInput, Toggle } from "./SettingsControls";
import { ReportSettings } from "./ReportSettings";
import { useSettings } from "./useSettings";

const LANGUAGES = [
    { code: "de", label: "Deutsch" },
    { code: "en", label: "English" },
];

export function GenericSettings({ showReports = true, minimal = false, onSaveRef, onDirty, onSaved }: { showReports?: boolean; minimal?: boolean; onSaveRef?: React.MutableRefObject<() => void>; onDirty?: (key: string) => void; onSaved?: (key: string) => void }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [pendingLang, setPendingLang] = useState(i18n.language.split("-")[0]);

    const {
        activityDays,
        setActivityDays,
        reportIntervalAmount,
        setReportIntervalAmount,
        reportIntervalUnit,
        setReportIntervalUnit,
        reportWeekday,
        setReportWeekday,
        reportMonthDay,
        setReportMonthDay,
        groupsEnabled,
        setGroupsEnabled,
        saved,
        save,
        dirty,
        markDirty,
    } = useSettings();

    const handleSave = async () => {
        i18n.changeLanguage(pendingLang);
        localStorage.setItem("lang", pendingLang);
        if (await save("generic", { activityDays, groupsEnabled })) onSaved?.("generic");
        if (showReports) {
            if (await save("reports", { reportIntervalAmount, reportIntervalUnit, reportWeekday, reportMonthDay })) onSaved?.("reports");
        }
    };

    const genericDirty = !!dirty["generic"];
    const reportsDirty = !!dirty["reports"];

    if (onSaveRef) onSaveRef.current = handleSave;

    return (
        <>
            <Section title={t("settings.generic")}>
                <SettingRow
                    title={t("language.title")}
                    description={t("language.description")}
                >
                    <div className="toggle-group">
                        {LANGUAGES.map((lang) => (
                            <button
                                key={lang.code}
                                className={`toggle-btn${pendingLang === lang.code ? " toggle-btn--active" : ""}`}
                                onClick={() => { setPendingLang(lang.code); markDirty("generic"); onDirty?.("generic"); }}
                            >
                                {lang.label}
                            </button>
                        ))}
                    </div>
                </SettingRow>

                <SettingRow title={t("settings.groupsEnabled")} description={t("settings.groupsEnabledDesc")}>
                    <Toggle
                        value={groupsEnabled}
                        onChange={(v) => { setGroupsEnabled(v); markDirty("generic"); onDirty?.("generic"); }}
                    />
                </SettingRow>
            </Section>

            {!minimal && <Section title={t("settings.dashboard")}>
                <SettingRow title={t("settings.activityDays")} description={t("settings.activityDaysDesc")}>
                    <div className="input-group">
                        <NumberInput
                            value={activityDays}
                            onChange={(v) => { setActivityDays(v); markDirty("generic"); onDirty?.("generic"); }}
                            onBlur={(v) => setActivityDays(Math.max(1, v))}
                            min={1}
                        />
                        <span className="settings-unit">{t("settings.days")}</span>
                    </div>
                </SettingRow>
            </Section>}

            {!minimal && <Section title={t("settings.subscription")}>
                <SettingRow title={t("settings.subscriptionManage")} description={t("settings.subscriptionManageDesc")}>
                    <button
                        className="btn-outline btn-outline--secondary"
                        onClick={() => navigate("/charges")}
                    >
                        {t("settings.subscriptionManageBtn")}
                    </button>
                </SettingRow>
            </Section>}

            {showReports && <ReportSettings
                intervalAmount={reportIntervalAmount}
                intervalUnit={reportIntervalUnit}
                weekday={reportWeekday}
                monthDay={reportMonthDay}
                saved={!!saved["reports"]}
                dirty={reportsDirty}
                onAmountChange={(v) => { setReportIntervalAmount(v); markDirty("reports"); onDirty?.("reports"); }}
                onUnitChange={(v) => { setReportIntervalUnit(v); markDirty("reports"); onDirty?.("reports"); }}
                onWeekdayChange={(v) => { setReportWeekday(v); markDirty("reports"); onDirty?.("reports"); }}
                onMonthDayChange={(v) => { setReportMonthDay(v); markDirty("reports"); onDirty?.("reports"); }}
            />}

        </>
    );
}
